import router from '@/router';
import {
  APIBadRequestError,
  APIConflictError,
  APIError,
  APIForbiddenError,
  APINotFoundError,
  AuthError,
} from '@/errors/error';
import { getRuntimeConfig } from '@erebus/runtime-config';
import { erebusHttp, legacyHttp, init } from '@erebus/http';

let isLoggingOut = false;

// The part of an axios error that handleError reads. The browser request
// (XMLHttpRequest) holds the HTTP status.
type HttpError = { request?: { status: number } | null } | null | undefined;

const handleError = (err: HttpError) => {
  return new Promise<void>((resolve, reject) => {
    if (err == null || err.request == null) {
      resolve();
      return;
    }
    const status = err.request.status;
    if (status === 0) {
      reject(new APIError(`API Error`, 'errors.apiError'));
    } else if (status === 401) {
      if (!isLoggingOut) {
        isLoggingOut = true;
        router.push({ name: 'logout' }).finally(() => {
          isLoggingOut = false;
        });
      }
      reject(new AuthError(`Auth Error - ${err}`, 'errors.authError'));
    } else if (status === 403) {
      // Forbidden means the user is signed in but may not do this. Do not log out.
      reject(
        new APIForbiddenError(`API Forbidden Error - ${err}`, 'errors.apiForbiddenError', status),
      );
    } else if (status === 404) {
      reject(
        new APINotFoundError(`API Not Found Error - ${err}`, 'errors.apiNotFoundError', status),
      );
    } else if (status === 400) {
      reject(
        new APIBadRequestError(
          `API Bad Request Error - ${err}`,
          'errors.apiBadRequestError',
          status,
        ),
      );
    } else if (status === 409) {
      reject(
        new APIConflictError(`API Conflict Error - ${err}`, 'errors.apiConflictError', status),
      );
    } else if (status === 500) {
      reject(new APIError(`API Error - ${err}`, 'errors.serverError', status));
    } else {
      reject(new APIError(`Error - ${err}`, 'errors.error', status));
    }
  });
};

const runtimeConfig = getRuntimeConfig();

// Cookie mode: the Go API reads the session cookie. Bearer mode: both APIs get
// the token saved at login (authStore) as an Authorization header.
if (runtimeConfig.useCookieAuth) {
  init();
} else {
  init({ bearerToken: () => localStorage.getItem('authToken') });

  // Cookie mode leaves errors raw; httpStatus.ts reads both shapes.
  erebusHttp.interceptors.response.use(undefined, (err) => handleError(err));
  legacyHttp.interceptors.response.use(undefined, (err) => handleError(err));
}

const legacyAxios = legacyHttp;
const erebus = erebusHttp;

export { legacyAxios, erebus };
